/**
 * Hermes exposes runtime counters on the (untyped) `HermesInternal` global. Everything here is
 * read defensively: the global is absent on web / JSC and the key set varies by Hermes version,
 * so the report copies whatever primitives are present rather than assuming a schema.
 */

type HermesInternalShape = {
  getInstrumentedStats?: () => Record<string, unknown>;
  getRuntimeProperties?: () => Record<string, unknown>;
};

function readHermesInternal(): HermesInternalShape | null {
  const candidate: unknown = Reflect.get(globalThis, "HermesInternal");
  if (candidate === null || typeof candidate !== "object") {
    return null;
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- shape checked field-by-field below
  return candidate as HermesInternalShape;
}

function readNumber(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export type JsHeapStats = {
  allocatedBytes: number | null;
  gcCount: number | null;
  gcTimeMs: number | null;
  heapSizeBytes: number | null;
  mallocBytes: number | null;
  totalAllocatedBytes: number | null;
};

/** Hermes heap/GC counters, or `null` when the runtime does not expose them. */
export function readJsHeapStats(): JsHeapStats | null {
  const hermes = readHermesInternal();
  if (hermes?.getInstrumentedStats === undefined) {
    return null;
  }
  let stats: Record<string, unknown>;
  try {
    stats = hermes.getInstrumentedStats();
  } catch {
    return null;
  }
  const gcTimeSeconds = readNumber(stats, "js_gcTime");
  return {
    allocatedBytes: readNumber(stats, "js_allocatedBytes"),
    gcCount: readNumber(stats, "js_numGCs"),
    // Hermes GCBase::getGCTime exposes seconds, despite other diagnostics using milliseconds.
    gcTimeMs: gcTimeSeconds === null ? null : gcTimeSeconds * 1000,
    heapSizeBytes: readNumber(stats, "js_heapSize"),
    mallocBytes: readNumber(stats, "js_mallocSizeEstimate"),
    totalAllocatedBytes: readNumber(stats, "js_totalAllocatedBytes"),
  };
}

/** Every primitive Hermes reports about itself (version, bytecode version, build flags). */
export function readHermesRuntimeProperties(): Record<string, unknown> | null {
  const hermes = readHermesInternal();
  if (hermes?.getRuntimeProperties === undefined) {
    return null;
  }
  try {
    const properties = hermes.getRuntimeProperties();
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(properties)) {
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        result[key] = value;
      }
    }
    const occupancyTarget: unknown = Reflect.get(globalThis, "__haniverseHermesOccupancyTarget");
    if (typeof occupancyTarget === "number" && Number.isFinite(occupancyTarget)) {
      result["Haniverse GC Occupancy Target"] = occupancyTarget;
    }
    return result;
  } catch {
    return null;
  }
}
